/**
 * Derelict reactivation, the survey card (plan S15; spec §16.2 the Harrow, §16.3 steps 1–2, §16.6, §16.9 bunker).
 *
 * A targeted scan with Scanner Mk II+ (`survey.minScannerTier`) of a revealed derelict gives the card: designation,
 * class, restorable or not (with the reason), the kit and refit lists, hazards, squatters (and the fence fee), a
 * Holdfast marker and its family, a sealed core ("needs power"), bunker tetherstone for an ore processor near a
 * vein, and the reboot defence. The Harrow gets its own card: "reactor: warm; structure: occupied" and an
 * in-fiction refusal; surveying it sets `harrow_surveyed` (an optional `watch` lead, outside the clue budget).
 *
 * `recordSurvey` writes a `surveyed` record for a restorable station (once; re-surveying changes nothing).
 * Pure; survey time only.
 */
import { createRng } from '../../core/rng'
import type { Career } from '../../career/types'
import { deriveSeed, intRange } from '../../galaxy/seed'
import type { Galaxy, Poi, StarSystem } from '../../galaxy/types'
import type { RelinkConfig } from '../../relink/config'
import { veinWithinHops } from '../../relink/rules'
import type { ReactivationConfig } from '../schemas'
import { setFlag, storyOf, withStory } from '../state'
import type { FrClass, StoryEffect, StoryResult } from '../types'
import {
  frStationOf,
  ineligibleReason,
  rebootParams,
  type EligibilityContext,
  type IneligibleReason,
  type RebootParams,
  type Squatters,
} from './eligibility'
import { kitRequirement, type KitRequirement } from './kit'

/** The refit an M4 construction site does after the restart (spec §16.3 step 7; S20 prices it). */
export interface RefitPlan {
  template: string
  costMult: number
  daysMult: number
}

export interface SurveyCard {
  kind: 'station' | 'harrow'
  poiId: string
  systemId: string
  designation: string
  /** Absent on the Harrow's card. */
  frClass?: FrClass
  /** Restorable now (seeded flag and exclusions). */
  restorable: boolean
  /** Why not, when not restorable. */
  reason: IneligibleReason | null
  /** The restart kit and refit (absent when not restorable). */
  kit?: KitRequirement
  refit?: RefitPlan
  coolantHazard: boolean
  squatters: Squatters
  /** Credits that pay a Wrecker squat off (0 without one). */
  fenceFee: number
  /** The family of a Holdfast generational claim marker, or null. */
  markerFamily: string | null
  /** A sealed core that opens once the station has power (a boot log, spec §16.6). */
  sealedCore: boolean
  /** First Rush-processed tetherstone in an ore processor near a vein (t; 0 = none). */
  bunkerT: number
  reboot?: RebootParams
  /** The Harrow's scan reading. */
  text?: string
  /** The in-fiction refusal when a restoration is tried (the Harrow). */
  refusal?: string
}

/** The targeted scan reads a survey card at this scanner tier or better (Mk II+). */
export function canSurvey(scannerTier: number, cfg: ReactivationConfig): boolean {
  return scannerTier >= cfg.survey.minScannerTier
}

/** Seeded bunker tetherstone (spec §16.9 rule 3): an ore processor within `bunker.veinHops` of a vein. */
export function bunkerTonnes(
  galaxy: Galaxy,
  system: Pick<StarSystem, 'id' | 'index'>,
  poiId: string,
  frClass: FrClass,
  cfg: ReactivationConfig,
): number {
  if (frClass !== 'oreProcessor' || !veinWithinHops(galaxy, system.id, cfg.bunker.veinHops)) return 0
  const rng = createRng(deriveSeed(galaxy.seed, 'reactivation-bunker', system.index, poiId))
  return intRange(rng, Math.ceil(cfg.bunker.minT), Math.floor(cfg.bunker.maxT))
}

/** The survey card for `poi` in `system` (the POI must be a derelict the player can target). */
export function surveyCard(
  galaxy: Galaxy,
  system: StarSystem,
  poi: Pick<Poi, 'id' | 'kind' | 'hidden'>,
  ctx: EligibilityContext,
  cfg: ReactivationConfig,
  relink: RelinkConfig,
): SurveyCard {
  if (poi.id === ctx.sites.harrowPoiId) {
    return {
      kind: 'harrow',
      poiId: poi.id,
      systemId: system.id,
      designation: `FR-${cfg.harrow.designation}`,
      restorable: false,
      reason: 'harrow',
      coolantHazard: false,
      squatters: 'none',
      fenceFee: 0,
      markerFamily: null,
      sealedCore: false,
      bunkerT: 0,
      text: cfg.harrow.surveyText,
      refusal: cfg.harrow.refusal,
    }
  }
  const st = frStationOf(galaxy, system, poi, cfg, ctx.sites)
  const reason = ineligibleReason(system, poi, st, ctx, cfg)
  const restorable = reason === null
  const card: SurveyCard = {
    kind: 'station',
    poiId: poi.id,
    systemId: system.id,
    designation: st.designation,
    frClass: st.frClass,
    restorable,
    reason,
    coolantHazard: restorable && st.coolantHazard,
    squatters: restorable ? st.squatters : 'none',
    fenceFee: restorable && st.squatters === 'wreckers' ? cfg.survey.fenceFee[system.region] : 0,
    markerFamily: restorable ? st.markerFamily : null,
    sealedCore: restorable,
    bunkerT: restorable ? bunkerTonnes(galaxy, system, poi.id, st.frClass, cfg) : 0,
  }
  if (restorable) {
    card.kit = kitRequirement(st.frClass, cfg)
    card.refit = {
      template: cfg.classes[st.frClass].template,
      costMult: cfg.refitMult,
      daysMult: cfg.refitDaysMult,
    }
    card.reboot = rebootParams(galaxy, system, relink)
  }
  return card
}

/**
 * Records a survey (spec §16.3 step 1): a restorable station gets a `surveyed` record with its class (once); the
 * Harrow sets `harrow_surveyed` and asks for an autosave. Anything else changes nothing.
 */
export function recordSurvey(career: Career, card: SurveyCard, onlineDay: number): StoryResult<Career> {
  const effects: StoryEffect[] = []
  if (card.kind === 'harrow') {
    if (storyOf(career).flags.harrow_surveyed === true) return { career, commands: [], effects }
    effects.push({ type: 'autosave', reason: 'harrowSurveyed' })
    return {
      career: setFlag(career, 'harrow_surveyed'),
      commands: [],
      effects,
    }
  }
  if (!card.restorable || card.frClass === undefined) return { career, commands: [], effects }
  if (storyOf(career).reactivations.some((r) => r.poiId === card.poiId)) return { career, commands: [], effects }
  const frClass = card.frClass
  const next = withStory(career, (s) => ({
    ...s,
    reactivations: [...s.reactivations, { poiId: card.poiId, state: 'surveyed', lastLitDay: onlineDay, frClass }],
  }))
  return { career: next, commands: [], effects }
}
